import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Car, UsersRound, Plus, Minus, Check } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import TimeDrum, { formatWindow as formatDrumWindow } from "@/apps/rider/components/booking/TimeDrum";
import TimeWheelPair from "@/apps/rider/components/booking/TimeWheelPair";
import StripeCheckoutSheet from "@/shared/components/StripeCheckoutSheet";
import { api } from "@/shared/lib/api";
import { formatTime12 } from "@/shared/lib/format";
import AddressInput from "@/shared/components/AddressInput";
import DestinationSuggestionChips from "@/apps/rider/components/booking/DestinationSuggestionChips";
import WindowSelect, { formatWindowLabel } from "@/apps/rider/components/WindowSelect";
import { surfaceStyle, surfaceClass, SURFACE_SPRING } from "@/shared/motion/surface";
import { AccountAvatarButton } from "@/apps/rider/components/AccountMorph";

const DAY_LABELS = [
  { id: "Mon", short: "M" },
  { id: "Tue", short: "T" },
  { id: "Wed", short: "W" },
  { id: "Thu", short: "T" },
  { id: "Fri", short: "F" },
  { id: "Sat", short: "S" },
  { id: "Sun", short: "S" },
];
const SEAT_DISPLAY_MAX = 5; // 5 (and any value ≥ 5) displays as "5+"

/**
 * Build the morph-pill copy for an upcoming trip leg returned by
 * /api/me/trip/upcoming.  Returns null when no leg is given or the leg
 * is malformed.  The shape is intentionally narrow so the pill stays
 * 56px tall and fits the 360px-wide morph state.
 *
 * Co-located with BookingSheet — only consumer in the codebase.
 */
function describeUpcomingLeg(trip) {
  if (!trip || !trip.date || !trip.pickup_time) return null;
  const direction = trip.direction === "return" ? "Depart by" : "Outbound";
  const dotColor = trip.direction === "return" ? "#0074d4" : "#ed6500";

  let whenLabel = "";
  try {
    const [yy, mm, dd] = trip.date.split("-").map(Number);
    const tripDate = new Date(yy, mm - 1, dd);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (sameDay(tripDate, today)) whenLabel = "Today";
    else if (sameDay(tripDate, tomorrow)) whenLabel = "Tomorrow";
    else whenLabel = tripDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch (_) {
    whenLabel = "";
  }

  const timeLabel = formatTime12 ? formatTime12(trip.pickup_time) : trip.pickup_time;
  const headline = whenLabel ? `${whenLabel} · ${timeLabel}` : timeLabel;

  const status = trip.status || "scheduled";
  let statusLabel = direction;
  let live = false;
  switch (status) {
    case "driver_assigned":  statusLabel = `${direction} · Driver assigned`; break;
    case "driver_en_route":  statusLabel = `${direction} · Driver on the way`; live = true; break;
    case "arriving_soon":    statusLabel = `${direction} · Arriving now`; live = true; break;
    case "in_progress":      statusLabel = `${direction} · Ride in progress`; live = true; break;
    default:                 statusLabel = direction;
  }
  return { directionLabel: direction, dotColor, whenLabel, headline, statusLabel, live };
}

/* =====================  BookingSheet — floating morph pill  =====================
   Single floating element hovering above the map.  NOT an edge-to-edge
   slide-up sheet.  Morphs width + height + border-radius between four
   states:

     collapsed  → ~76px pill (pickup field only)
     addressing → ~232px floating card (pickup + destination)
     calc       → same as addressing with an inline shimmer row
     ready      → ~420px floating card with route summary + price + Slide CTA

   Pill is centered horizontally with margin from viewport edges + floats
   above the bottom MobileTabBar (clearance: safe-area + 12 + 63 + 16).
   All four corners are rounded; no edge-to-edge connection to the screen
   border — the map shows through around it.

   Tap-on-map collapses.  Drag-down on the top of the expanded pill also
   collapses.  Entered values survive state transitions.
*/
export default function BookingSheet({
  state,
  pickup, setPickup,
  destination, setDestination,
  onPickupOpenChange, onDestOpenChange,
  focusPickup,
  focusDest,
  onExpand, onCollapse,
  currentTier, distance, duration, previewRate,
  // P0 · single-ride price bug + P2 · pinned price row — the wizard now
  // needs the SAME totals the intent will be created with so the price
  // shown mid-flow reconciles to the amount Stripe charges (no more
  // pre-discount daily rate leaking through Step 3).
  cycleTotal, firstRideDiscount, finalTotal, dailyRate,
  // Feb 2026 fare bundle from the server — receipt-in-progress detail
  // (party mult, volume + subscribe rates, first-ride, floor, nudges).
  // Used by BookingWizard Steps 1/2 for the live rate breakdown panel.
  fare,
  onConfirm, flipping, upcomingTrip,
  // P1 · in-sheet payment — Step 3 mints the route eagerly on entry and
  // mounts <StripeCheckoutSheet chromeless> so the rider never leaves.
  onCreateRoute,
  onPaymentSuccess,
  // ---- Inline transportation plan (route-card-as-plan) ----
  // Plan-mode: when these handlers are present, the ready state renders
  // the full transportation plan (days, windows, seats, preference) and
  // slide-to-schedule submits the route directly.
  days, setDays,
  pickupWindow, setPickupWindow,
  returnWindow, setReturnWindow,
  seats, setSeats,
  pref, setPref,
  // Billing-cycle toggle — flat plan card needs to expose Pay once vs
  // Weekly plan so single-day riders aren't silently forced into a
  // subscription. Optional: when omitted, the toggle just isn't rendered.
  cycle, setCycle, setCycleUserPicked,
  submitting,
  // Rider first name for personalized entry title. When available, the
  // "Where to?" title becomes "Where to, {firstName}?" so returning
  // riders feel recognized. Passed through from ScheduleSetup which
  // pulls it from the auth user record.
  riderFirstName,
  // Address Entry rebuild — explicit Continue affordance in the typing
  // card. Auto-advance to route-draw still fires via ScheduleSetup's
  // debounced getTier effect once both addresses look real; this button
  // is a discoverable tap target for riders who paste or type without
  // picking a suggestion. Optional — sheet works without it wired.
  onContinue,
  // Address-entry CTA label override. Default "Continue" preserves
  // the legacy wizard hand-off; the new Book (05) → Your Week (06)
  // flow passes "See price" so the single #ED6500 pill on the sheet
  // reads as the price-reveal action.
  continueCtaLabel = "Continue",
  continueCtaTestId,
  // Hand-off used by the wizard Back button on Step 0 (and the
  // swipe-down gesture on any wizard step) to return the rider to
  // the "Where to?" entry surface. ScheduleSetup toggles an
  // `entryOverride` flag so the polyline stays drawn and the wizard
  // state is preserved for a subsequent swipe-up return.
  onExitToEntry,
  // Complement to `onExitToEntry` — the swipe-up gesture on the
  // address entry card fires this to slide back to the wizard step
  // the rider left. Only wired when `canReturnToRoute` is true (a
  // route is minted and the rider is currently in the entry
  // override).
  onReturnToRoute,
  canReturnToRoute = false,
  // B3.5 Task 1 — dynamic first-ride discount payload from
  // /api/pricing/first-ride. Consumed by Step 3 (Review) to render an
  // accurate banner ("X% off" or "$X off") that mirrors the actual coupon.
  // Optional initial wizard step. Used by "Repeat last week" flow to
  // jump straight to Review & Pay (idx 2) after prefilling the plan.
  initialWizardStepIdx = 0,
  firstRide,
}) {
  const planMode = typeof setDays === "function"
    && typeof setPickupWindow === "function"
    && typeof setReturnWindow === "function"
    && typeof setSeats === "function"
    && typeof setPref === "function";
  const planReady = planMode
    && Array.isArray(days) && days.length > 0
    && !!pickupWindow && !!returnWindow
    && (seats || 1) >= 1
    && (pref === "shared" || pref === "private");

  const nav = useNavigate();
  // B3 · Reduce-motion detector — used to gate the sheet spring overshoot
  // and other framer animations inside this file. Framer's useReducedMotion
  // subscribes to the `prefers-reduced-motion` media query and re-renders
  // when it flips, so the sheet's transition string updates live.
  const reduceMotion = useReducedMotion();
  // Edit-Route affordance — when true (only meaningful in `ready` state),
  // the pickup + destination AddressInputs re-mount so the rider can change
  // a leg. Reclaiming this vertical space is the whole point of the
  // compact route summary that replaces them by default.
  const [editRouteOpen, setEditRouteOpen] = useState(false);
  // Snap back to compact route summary whenever we (re)enter `ready` from
  // any other state (e.g. after the rider just calculated a new route).
  useEffect(() => { if (state !== "ready") setEditRouteOpen(false); }, [state]);
  // Multi-ride support: the upcoming-trip chip is now ALWAYS additive —
  // it floats as a small chip above the booking pill, never replaces it.
  // Riders with an existing scheduled ride can still tap the booking pill
  // to schedule another. (Previously: tripActive replaced the entire
  // collapsed pill with a non-bookable trip-status banner — that gated
  // multi-ride bookings and had to go.)
  const tripMeta = useMemo(() => describeUpcomingLeg(upcomingTrip), [upcomingTrip]);
  const showTripChip = state === "collapsed" && !!upcomingTrip && !pickup && !destination;
  const pickupShort = pickup ? pickup.split(",")[0].trim() : "";
  const destShort = destination ? destination.split(",")[0].trim() : "";
  // Continue-button gate — Address Entry rebuild. Both fields must look
  // like a real, autocomplete-shaped address (contains a comma, ≥12 chars)
  // so tapping Continue doesn't kick off a route quote for a half-typed
  // string. Mirrors the `looksLikeAddress` gate in ScheduleSetup.
  const bothLookReal = useMemo(() => {
    const looks = (s) => !!s && s.trim().includes(",") && s.trim().length >= 12;
    return looks(pickup) && looks(destination);
  }, [pickup, destination]);

  // Morph shape configs per state. Width + radius transition together on
  // the same 320ms cubic-bezier so the pill reshapes as one fluid piece.
  // `calc` and `chip` fold the sheet up — the outer container is
  // translated off-screen so the map has the entire frame while the
  // route draws. `calc` keeps the shape values so height animates
  // smoothly if the state pings back to `addressing`. `ready` height is
  // `auto` so the route card fits the full plan card.
  const SHAPE = {
    collapsed:  { width: "min(420px, calc(100vw - 24px))", height: 76,     radius: 38 },
    addressing: { width: "min(480px, calc(100vw - 24px))", height: "auto", radius: 24 },
    // calc + chip inherit the addressing shape so the card doesn't
    // visibly resize mid-exit. The card is sliding OFF-screen — height
    // matters only until it clears the fold. Snapping height to 232px
    // just before the translate begins was contributing to the
    // "disappear and reappear" perception on device.
    calc:       { width: "min(480px, calc(100vw - 24px))", height: "auto", radius: 24 },
    chip:       { width: "min(480px, calc(100vw - 24px))", height: "auto", radius: 24 },
    ready:      { width: "min(560px, calc(100vw - 24px))", height: "auto", radius: 28 },
  };
  // Sequence 1 → 2 chain — during `calc` (route drawing) and `chip`
  // (result chip visible), the entry card exits the frame downward so
  // the map owns the screen. The sheet rises back on `ready` at the
  // SAME time the frost engages (see ScheduleSetup: both use --motion-slow
  // with the same ease-out curve).
  const sheetExited = state === "calc" || state === "chip";

  // While the sheet is exiting off-screen (`calc` / `chip`), we KEEP
  // rendering whatever content was visible immediately before the
  // exit began. Without this latch the block unmounted the instant
  // state flipped, so the card visibly emptied out BEFORE the
  // translate could carry it off — the "disappear and reappear" the
  // user reported.
  //
  // Ref-based (not state-based) so we don't trigger an extra render
  // when the latch value changes. Semantics:
  //   • Forward pass (addressing → calc → ready): before exit, state
  //     is "addressing" → ref latches "addressing". During calc the
  //     card slides down with addressing content. On ready the sheet
  //     slides back in with wizard content.
  //   • Backward pass (ready → calc → addressing): before exit,
  //     state is "ready" → ref latches "ready". During calc the card
  //     slides down with wizard content still visible. On addressing
  //     the sheet slides back in with the address entry rows.
  const lastVisualStateRef = useRef(state);
  useEffect(() => {
    if (!sheetExited) lastVisualStateRef.current = state;
  }, [sheetExited, state]);
  const contentState = sheetExited ? lastVisualStateRef.current : state;

  // Shape tracks `contentState` so during a hand-off exit the sheet
  // keeps its pre-exit width/radius (no width morph mid-slide) and
  // the shape transition plays on the visible entrance instead.
  const shape = SHAPE[contentState] || SHAPE[state] || SHAPE.collapsed;

  // Bidirectional drag on the top affordance:
  //   • state === "ready"      → drag DOWN past threshold → onExitToEntry
  //                              (slides the sheet back to the address
  //                               entry surface; polyline stays drawn)
  //   • state === "addressing" → drag UP past threshold → onReturnToRoute
  //                              (only when `canReturnToRoute` is true,
  //                               i.e. a minted route waits to be
  //                               returned to; slides back to the wizard)
  // Uses window-level pointer listeners (no setPointerCapture) so the
  // gesture completes even if the pointer exits the affordance area.
  // Wizard step index — hoisted here from BookingWizard so it survives
  // the conditional unmount when the rider swipes down to return to
  // the address entry (`state === "ready"` flips false and the wizard
  // unmounts). Swiping back UP now returns them to the exact step they
  // left instead of resetting to Step 1.
  const [wizardStepIdx, setWizardStepIdx] = useState(initialWizardStepIdx || 0);
  // "Repeat last week" — the parent sets `initialWizardStepIdx = 2`
  // AFTER our first render (preset-read runs in a useEffect). Sync
  // the wizard step once when the parent flips it, so we don't stay
  // stuck on Step 0.
  const initialStepAppliedRef = useRef(false);
  useEffect(() => {
    if (initialStepAppliedRef.current) return;
    if (!initialWizardStepIdx) return;
    initialStepAppliedRef.current = true;
    setWizardStepIdx(initialWizardStepIdx);
  }, [initialWizardStepIdx]);
  const SWIPE_THRESHOLD = 60;
  const startYRef = useRef(null);
  const draggingRef = useRef(false);
  const [dragDY, setDragDY] = useState(0);
  const endDragRef = useRef(null);
  // Called from the sheet body's onPointerDown. Filters out gestures
  // that start on interactive elements (inputs, buttons, links) or
  // inside a scrollable container so the drag doesn't fight scroll
  // or hijack taps on the wizard buttons.
  const onPointerDown = (e) => {
    if (state === "collapsed") return;
    const target = e.target;
    if (!target || !(target instanceof Element)) return;
    // Skip on interactive elements — buttons, inputs, links.
    if (target.closest("button, input, textarea, select, a, [role='button']")) return;
    // Skip if the touch started inside a horizontally-scrollable strip
    // (day tiles, time wheels) — those need their own gestures.
    if (target.closest("[data-no-sheet-drag]")) return;
    draggingRef.current = true;
    startYRef.current = e.clientY;
  };
  const endDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const dy = dragDY;
    setDragDY(0);
    startYRef.current = null;
    if (state === "ready" && dy > SWIPE_THRESHOLD) {
      // Drag-down from wizard → return to entry (slide down morph).
      if (typeof onExitToEntry === "function") onExitToEntry();
    } else if (state === "addressing" && canReturnToRoute && dy < -SWIPE_THRESHOLD) {
      // Drag-up from entry → return to the wizard step you left.
      if (typeof onReturnToRoute === "function") onReturnToRoute();
    }
  };
  endDragRef.current = endDrag;
  useEffect(() => {
    const moveHandler = (e) => {
      if (!draggingRef.current || startYRef.current == null) return;
      const clientY = e.touches ? e.touches[0]?.clientY : e.clientY;
      if (clientY == null) return;
      const dy = clientY - startYRef.current;
      // Allow both directions — down for ready→entry, up for entry→ready.
      // Cap the negative direction only when we can actually return.
      const clamped = dy < 0 && !canReturnToRoute && state === "addressing" ? 0 : dy;
      setDragDY(clamped);
    };
    const upHandler = () => { endDragRef.current?.(); };
    window.addEventListener("pointermove", moveHandler, { passive: true });
    window.addEventListener("pointerup", upHandler);
    window.addEventListener("pointercancel", upHandler);
    return () => {
      window.removeEventListener("pointermove", moveHandler);
      window.removeEventListener("pointerup", upHandler);
      window.removeEventListener("pointercancel", upHandler);
    };
  }, [canReturnToRoute, state]);

  // Route-ready earliest-pickup fetch — only used by the legacy non-plan
  // fallback (see PlanFallbackEarliest at file end). Moved out of the main
  // component so plan-mode (the default home flow) avoids the extra fetch.

  // Publish pill clearance (bottom offset + height + margin) so floating
  // peers (recenter button, toasts) sit above without overlap. When the
  // plan card is in `ready` (auto-height), publish a generous fallback so
  // map-floats don't crowd the card during the rare moment before its
  // measured height is known.
  useEffect(() => {
    const root = document.documentElement;
    const h = typeof shape.height === "number" ? shape.height : 560;
    root.style.setProperty("--link-sheet-height", `${h + 32}px`);
    return () => { root.style.removeProperty("--link-sheet-height"); };
  }, [shape.height]);

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 pointer-events-none flex flex-col items-center gap-2"
      style={{
        // BOTTOM sheet — the card's bottom edge sits on the home-
        // indicator / safe-area bottom with a 16px breathing gap so
        // it never touches the nav. The map behind stays full-bleed;
        // the sheet is a bottom-anchored floating card, never docked
        // to the status bar.
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
        // Sit ABOVE the top floating rider account pill (z-50) so the
        // expanded "ready" booking card always floats on top, never
        // clipped beneath the account chrome.
        zIndex: 60,
      }}
      data-testid="booking-sheet-wrap"
    >
      {/* Upcoming-trip chip — non-blocking. Floats just above the booking
          pill whenever the rider has a scheduled ride AND the booking pill
          is in its collapsed/idle state. Tapping the chip jumps to /trips
          to view the existing ride; tapping the booking pill below still
          books a new one. Multi-ride support: the chip never replaces the
          booking pill or gates the booking flow.

          Hidden in expanded states (addressing/calc/ready) to keep the
          map breathing room around the active workflow. */}
      {showTripChip && tripMeta && (
        <button
          type="button"
          onClick={() => nav("/trips")}
          className={`pointer-events-auto bg-[#0A1128]/85 text-[#F9F7F4] flex items-center gap-2.5 pl-3 pr-2.5 py-2 rounded-full group ${surfaceClass()}`}
          style={{ maxWidth: "min(420px, calc(100vw - 24px))", ...surfaceStyle("chip", { theme: "dark" }) }}
          data-testid="sheet-trip-pill"
          aria-label={tripMeta ? `${tripMeta.directionLabel} ${tripMeta.whenLabel} — view trip` : "View upcoming trip"}
        >
          <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
            {tripMeta.live && (
              <span className="absolute inset-0 rounded-full bg-[#ed6500] opacity-75 animate-ping" />
            )}
            <span
              className="relative inline-flex rounded-full h-2 w-2"
              style={{ background: tripMeta.dotColor || "#ed6500" }}
            />
          </span>
          <span className="text-left min-w-0">
            <span className="block text-[9px] tracking-[0.22em] uppercase font-semibold text-[#F9F7F4]/55 leading-none">
              {tripMeta.statusLabel || "Upcoming"}
            </span>
            <span
              className="block text-[12px] mt-0.5 leading-none truncate max-w-[260px]"
              data-testid="sheet-trip-pill-headline"
            >
              {tripMeta.headline || "Your next ride"}
            </span>
          </span>
          <ArrowRight
            size={12}
            className="text-[#ed6500] shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </button>
      )}

      {/* ===== RESULT CHIP =====
          Rises from below on `chip` state (after the polyline finishes
          drawing) and holds ~1s so the rider reads the numbers. Fades
          out as the sheet begins its `ready` rise. Chip motion + sheet
          rise share the same duration + easing so the chip's exit and
          the sheet's entry read as one continuous hand-off.
          Kept inline (no new component) — it's a single presentational
          span, gated by state. */}
      {/* Chip preview pill was removed per user direction — the
          polyline draw + pin pop are the entire visual event during
          the calc hand-off. State machine skips `drawing → settled`. */}

      <motion.div
        className={`pointer-events-auto text-[#F9F7F4] ${surfaceClass()} ${flipping ? "animate-card-flipaway" : ""}`}
        // Framer-motion spring on the shape morph. `SURFACE_SPRING.card`
        // is the same spring the account pill / MorphPill / other
        // floating surfaces use, so the entry card lands in the shared
        // motion family instead of a bespoke CSS curve.
        animate={{
          width: shape.width,
          height: shape.height,
          borderRadius: shape.radius,
        }}
        transition={reduceMotion ? { duration: 0 } : SURFACE_SPRING.card}
        style={{
          // Glass card. During `idle` (collapsed) and `addressing` the
          // map behind is fully clear (no frost overlay), so the sheet
          // itself must be DARK glass to keep the cream/white text
          // legible over any map tile. On `ready` the full-screen frost
          // overlay renders a dark backdrop, so the sheet flips to a
          // lighter white-tint frosted glass (per step1e spec — map +
          // polyline sensed through the sheet).
          //
          // Background/border track `contentState` (NOT `state`) so
          // that during a hand-off exit the card keeps whichever look
          // it had before the slide started — no colour snap
          // mid-flight. Forward pass slides down with dark-navy
          // addressing look; backward pass slides down with light
          // frost wizard look.
          background: contentState === "ready"
            ? "rgba(255,255,255,0.05)"
            : "rgba(10,17,40,0.72)",
          backdropFilter: "blur(30px) saturate(1.25)",
          WebkitBackdropFilter: "blur(30px) saturate(1.25)",
          maxHeight: contentState === "ready"
            ? // Top clearance = safe-area + 12 (pill top offset) + 76 (pill
              // height) + 20 (breathing gap) = safe-area + 108px. Prevents
              // the ready-state sheet from creeping up under the account
              // pill on tall content (Step 3 Review & Pay + payment).
              "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px - 108px)"
            : contentState === "addressing" || contentState === "calc" || contentState === "chip"
              ? // Typing state — card grows to fit the fields + inline
                // dark-glass suggestions + Continue button, but never
                // extends into the top-pill zone or the bottom safe-area
                // gutter. Suggestions inside the card have their own
                // internal scroll capped at ~3 rows, so the card itself
                // rarely maxes out unless the keyboard is closed.
                "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px - 108px)"
              : undefined,
          borderRadius: shape.radius,
          // Sequence 1 & 2 hand-off. During calc/chip the sheet slides
          // DOWN and OUT so the map owns the frame while the polyline
          // draws. On `ready` the sheet slides back up with the SAME
          // duration and easing as the frost so both land together.
          //
          // Uses the CSS `translate` property (individual transform
          // property, spec Level 2) so it doesn't stomp any transform
          // framer-motion may write internally. Drag still uses
          // `transform: translateY(...)` — `translate` and `transform`
          // compose, so drag and exit-slide never fight for the same
          // property.
          translate: dragDY
            ? undefined
            : sheetExited
              ? "0 calc(100% + 24px)"
              : "0 0",
          transform: dragDY ? `translateY(${dragDY}px)` : undefined,
          opacity: sheetExited ? 0 : 1,
          transition: dragDY
            ? "none"
            : reduceMotion
              ? "translate 220ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms cubic-bezier(0.4, 0, 0.2, 1)"
              : "translate 420ms cubic-bezier(0.32, 0.72, 0, 1), opacity 420ms cubic-bezier(0.32, 0.72, 0, 1)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          ...surfaceStyle(state === "collapsed" ? "chip" : "card"),
          // Round 5 · Match the scheduler's "alive" white-glow feel on
          // the address entry card. Dark theme surface + 45% white 1px
          // border. Kept across calc/chip so the border doesn't blink
          // off mid-exit — only `ready` drops it (the ready wizard
          // wraps its own surfaces). Also uses `contentState` so a
          // backward hand-off from ready doesn't briefly flash the
          // border in.
          border: contentState !== "ready"
            ? "1px solid rgba(255, 255, 255, 0.45)"
            : undefined,
        }}
        data-testid="booking-sheet"
        data-sheet-state={state}
        data-spring-overshoot={reduceMotion ? "off" : "on"}
      >
        {/* ===== COLLAPSED — single pickup pill, tap to expand =====
            Always renders in collapsed state regardless of upcoming-trip
            presence (multi-ride: schedule another ride anytime). The
            placeholder swaps to "Schedule another ride" when an upcoming
            trip exists so the affordance is unambiguous. */}
        {state === "collapsed" && (
          <button
            type="button"
            onClick={onExpand}
            className="w-full h-full flex items-center gap-3 px-5 text-left"
            data-testid="sheet-collapsed-pill"
            aria-label={upcomingTrip ? "Schedule another ride" : "Start your commute — tap to enter pickup"}
          >
            <span className="w-2 h-2 rounded-full bg-[#0074d4] shrink-0" aria-hidden="true" />
            <span className="flex-1 min-w-0 truncate text-[15px]">
              {pickup ? (
                <span className="text-[#F9F7F4]">{pickupShort}</span>
              ) : upcomingTrip ? (
                <span className="text-[#F9F7F4]" data-testid="sheet-cta-schedule-another">Schedule another ride</span>
              ) : (
                <span className="text-[#F9F7F4]/55">Home or pickup location</span>
              )}
            </span>
            <ArrowRight size={14} className="text-[#ed6500] shrink-0" aria-hidden="true" />
          </button>
        )}

        {/* ===== CALC / CHIP =====
             Sheet is off-screen during these phases so the map has the
             frame to itself while the polyline draws. Kept as empty
             render slots so the container's translate/opacity animate
             cleanly through the state — no DOM churn, no status label.
             The rising result chip is a sibling of this container (see
             below); the animation IS the feedback. */}

        {/* ===== EXPANDED (addressing / ready) =====
            During `calc` / `chip` the outer motion.div slides off-
            screen; we KEEP rendering the addressing content
            (`contentState === "addressing"`) so the card slides down
            as a fully-populated slab instead of emptying out mid-
            flight. Pointer events are disabled while exiting to
            prevent stray taps on the invisible card. */}
        {state !== "collapsed" && (
          <div
            className="flex-1 flex flex-col min-h-0"
            style={{ pointerEvents: sheetExited ? "none" : undefined }}
            aria-hidden={sheetExited || undefined}
          >
            {/* Drag zone — the top strip of the sheet, including the
                thin handle bar and generous surrounding whitespace, is
                the entire swipe-down / swipe-up target. Previously
                only the tiny 40×4 bar received pointer events, which
                made the swipe feel unreliable — riders would touch a
                pixel above/below the handle and the gesture would fall
                through to the map. The zone now spans the full sheet
                width and ~32px vertical, so anywhere near the top of
                the sheet initiates the drag. */}
            <div
              className="pt-2 pb-2 flex justify-center shrink-0"
              style={{ touchAction: "none", cursor: "grab" }}
              onPointerDown={onPointerDown}
              data-testid="sheet-drag-affordance"
            >
              <span className="h-1 w-12 rounded-full bg-white/35" aria-hidden="true" />
            </div>

            {/* Sheet chrome — wordmark left, account avatar right.
                Renders on the sheet (not floating above) so the top
                of the screen stays clear. The avatar uses the shared
                `layoutId` morph so tapping it grows this card into
                the full-screen account overlay without unmounting the
                booking flow. */}
            <div
              className="flex items-center justify-between px-4 pt-1 pb-3 shrink-0"
              data-testid="sheet-header-chrome"
            >
              <img
                src="/assets/link-full-logo.svg"
                alt="Link"
                style={{ height: 22 }}
                data-testid="sheet-header-logo"
              />
              <AccountAvatarButton testId="sheet-header-avatar" size={36} />
            </div>

            {/* Pickup → Destination breadcrumb pill — ONLY shown in the
                `ready` state where it becomes the compact route summary
                that replaces the full AddressInput rows (Edit toggles
                them back). Deleted from the `addressing` state per
                user feedback: the two input fields directly below
                already show the addresses, so a preview pill above
                them was redundant. */}
            {contentState === "ready" && pickup && destination && (
              <div className="px-4 pb-1 shrink-0 flex items-center justify-between gap-3" data-testid="sheet-route-pill">
                <div className="inline-flex items-center gap-2 text-[11px] tracking-wide bg-white/5 border border-white/10 rounded-full px-3 py-1 min-w-0 flex-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4B9CF8] shrink-0" />
                  <span className="truncate flex-1 min-w-0">{pickupShort}</span>
                  <ArrowRight size={11} className="text-[#ff7d22] shrink-0" />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#ff7d22] shrink-0" />
                  <span className="truncate flex-1 min-w-0">{destShort}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setEditRouteOpen((v) => !v)}
                  className="text-[10px] tracking-[0.22em] uppercase text-white/65 hover:text-[#ed6500] transition-colors shrink-0 px-3 py-1.5 rounded-full border border-white/12 hover:border-[#ed6500]/40"
                  data-testid="sheet-edit-route"
                >
                  {editRouteOpen ? "Done" : "Edit"}
                </button>
              </div>
            )}

            <div
              className={`flex-1 min-h-0 px-4 pb-4 ${
                contentState === "ready"
                  ? "overflow-y-auto"
                  : contentState === "addressing"
                    ? "overflow-y-auto"
                    : "overflow-hidden"
              }`}
            >
              {/* Pickup + destination input rows. Visible while addressing,
                  calculating, and in `ready` ONLY when the rider has tapped
                  Edit Route to change a leg. This reclaims vertical space
                  for the rest of the transportation plan. */}
              {(contentState !== "ready" || editRouteOpen) && (
                <>
                  {/* "Where to?" title — matches the scheduler wizard's
                      display headline family (Montserrat 900 with the
                      Link brand blue→orange gradient). Rendered only
                      during addressing so the ready-state wizard keeps
                      its per-step headline. */}
                  {contentState === "addressing" && (
                    <h2
                      className="mt-1 mb-2"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 900,
                        fontSize: 28,
                        lineHeight: 1.05,
                        letterSpacing: "-0.9px",
                        color: "#F9F7F4",
                      }}
                      data-testid="entry-title"
                    >
                      <span
                        style={{
                          background: "linear-gradient(100deg,#4B9CF8,#ff7d22)",
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          color: "transparent",
                        }}
                        data-testid="entry-title-gradient"
                      >
                        Where to
                        {riderFirstName ? "," : "?"}
                      </span>
                      {riderFirstName && (
                        <span data-testid="entry-title-name" style={{ color: "#F9F7F4" }}>
                          {" "}{riderFirstName}?
                        </span>
                      )}
                    </h2>
                  )}
                  <div className="mt-1" data-testid="sheet-pickup-row">
                    <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-3.5 py-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0074d4] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <AddressInput
                          value={pickup}
                          onChange={setPickup}
                          placeholder="Home or pickup location"
                          allowCurrentLocation
                          testId="input-pickup-address"
                          onOpenChange={onPickupOpenChange}
                          autoFocus={focusPickup}
                          variant="dark"
                          inlineSuggestions={contentState === "addressing"}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-2" data-testid="sheet-dest-row">
                    <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-3.5 py-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#ed6500] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <AddressInput
                          value={destination}
                          onChange={setDestination}
                          placeholder="Work or destination"
                          testId="input-destination-address"
                          onOpenChange={onDestOpenChange}
                          autoFocus={focusDest}
                          variant="dark"
                          inlineSuggestions={contentState === "addressing"}
                        />
                      </div>
                    </div>
                    <DestinationSuggestionChips
                      visible={contentState === "addressing" && !!pickup && !destination}
                      onPick={(addr) => setDestination(addr)}
                      testId="sheet-dest-suggestions"
                    />
                  </div>

                  {/* Continue button — Address Entry rebuild spec.
                      Idle (fields empty) → disabled. Both fields filled
                      with a real address → enabled; tapping hands off to
                      the route-draw sequence (getTier is auto-called via
                      the debounced effect in ScheduleSetup once both
                      addresses look complete, so tapping Continue is
                      primarily an explicit affordance). Rendered only in
                      the addressing state so the ready wizard keeps its
                      own step nav. */}
                  {contentState === "addressing" && (
                    <button
                      type="button"
                      onClick={() => {
                        try { document.activeElement?.blur?.(); } catch (_) {}
                        if (typeof onContinue === "function") onContinue();
                      }}
                      disabled={!bothLookReal}
                      className="mt-3 w-full h-12 rounded-full text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors"
                      style={{
                        background: bothLookReal ? "#ed6500" : "rgba(255,255,255,0.06)",
                        color: bothLookReal ? "#fff" : "rgba(249,247,244,0.35)",
                        cursor: bothLookReal ? "pointer" : "not-allowed",
                        // Montserrat display face on the CTA to match
                        // the entry title + wizard step headlines.
                        fontFamily: "var(--font-display)",
                        letterSpacing: "0.02em",
                      }}
                      data-testid={continueCtaTestId || "entry-continue-button"}
                      aria-label={continueCtaLabel === "Continue" ? "Continue to route" : continueCtaLabel}
                    >
                      {continueCtaLabel}
                      {bothLookReal && <ArrowRight size={15} strokeWidth={2.2} />}
                    </button>
                  )}
                </>
              )}

              {/* B3.5 — Ready state redesigned into 3-step wizard:
                    Step 1 · When     (day pills w/ real dates + windows)
                    Step 2 · How      (seats + preference + billing)
                    Step 3 · Review   (summary + promo banner + price + tap CTA)
                  Steps slide directionally (respects prefers-reduced-motion).
                  Zero scroll required at 390/430/768 widths. */}
              {contentState === "ready" && (
                <BookingWizard
                  stepIdx={wizardStepIdx}
                  setStepIdx={setWizardStepIdx}
                  planMode={planMode}
                  planReady={planReady}
                  submitting={submitting}
                  distance={distance}
                  duration={duration}
                  currentTier={currentTier}
                  previewRate={previewRate}
                  cycleTotal={cycleTotal}
                  firstRideDiscount={firstRideDiscount}
                  finalTotal={finalTotal}
                  dailyRate={dailyRate}
                  fare={fare}
                  days={days} setDays={setDays}
                  pickupWindow={pickupWindow} setPickupWindow={setPickupWindow}
                  returnWindow={returnWindow} setReturnWindow={setReturnWindow}
                  seats={seats} setSeats={setSeats}
                  pref={pref} setPref={setPref}
                  cycle={cycle} setCycle={setCycle} setCycleUserPicked={setCycleUserPicked}
                  pickup={pickup} destination={destination}
                  firstRide={firstRide}
                  onConfirm={onConfirm}
                  onCreateRoute={onCreateRoute}
                  onPaymentSuccess={onPaymentSuccess}
                  onExitToEntry={onExitToEntry}
                />
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

/* =====================  Plan sub-components  ===================== */

function PlanField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#F9F7F4]/55 mb-1.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function PreferenceCard({ active, onClick, icon, title, sub, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-2xl border p-3 transition-colors ${
        active
          ? "border-[#ed6500] bg-[#ed6500]/10"
          : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]"
      }`}
      data-testid={testId}
      aria-pressed={active}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={active ? "text-[#ed6500]" : "text-white/70"}>{icon}</span>
        <span className="text-[13px] font-semibold tracking-wide text-[#F9F7F4]">{title}</span>
      </div>
      <div className="text-[11px] leading-snug text-[#F9F7F4]/60">{sub}</div>
    </button>
  );
}

const DAY_FULL = { Mon: "Mon", Tue: "Tue", Wed: "Wed", Thu: "Thu", Fri: "Fri", Sat: "Sat", Sun: "Sun" };
const DAY_ORDER_KEY = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

/**
 * SeatsStepper — `[-] N [+]` quantity control. Replaces the row of seat
 * chips that consumed too much horizontal space on small viewports.
 * Display rule: value >= SEAT_DISPLAY_MAX renders as "5+", but the stored
 * value caps at SEAT_DISPLAY_MAX so backend math stays well-defined.
 *
 * Touch targets sized for thumb (48×48 px buttons, h-12). Designed for
 * one-handed mobile use — bigger than typical text-button densities.
 */
function SeatsStepper({ value = 1, onChange, min = 1, max = SEAT_DISPLAY_MAX, testId = "plan-seats" }) {
  const v = Math.max(min, Math.min(max, Number(value) || min));
  const label = v >= SEAT_DISPLAY_MAX ? `${SEAT_DISPLAY_MAX}+` : String(v);
  const dec = () => onChange?.(Math.max(min, v - 1));
  const inc = () => onChange?.(Math.min(max, v + 1));
  return (
    <div
      className="inline-flex items-center gap-3 bg-white/[0.05] border border-white/10 rounded-2xl p-2"
      data-testid={testId}
      data-seats-value={v}
    >
      <button
        type="button"
        onClick={dec}
        disabled={v <= min}
        aria-label="Decrease seats"
        className="w-12 h-12 rounded-2xl bg-white/[0.07] hover:bg-white/[0.14] active:bg-white/[0.18] text-[#F9F7F4] disabled:opacity-25 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        data-testid={`${testId}-decrement`}
      >
        <Minus size={20} strokeWidth={2.2} />
      </button>
      <div
        className="min-w-[4rem] text-center font-serif text-[26px] tabular-nums text-[#F9F7F4] select-none"
        data-testid={`${testId}-value`}
      >
        {label}
      </div>
      <button
        type="button"
        onClick={inc}
        disabled={v >= max}
        aria-label="Increase seats"
        className="w-12 h-12 rounded-2xl bg-white/[0.07] hover:bg-white/[0.14] active:bg-white/[0.18] text-[#F9F7F4] disabled:opacity-25 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        data-testid={`${testId}-increment`}
      >
        <Plus size={20} strokeWidth={2.2} />
      </button>
    </div>
  );
}


/**
 * PlanSummary — final-review block above the action button. Sorts days
 * Mon→Sun and shows a "—" placeholder for any field the rider hasn't set,
 * so the block never looks empty/broken before all selections are made.
 */
function PlanSummary({ days, pickupWindow, returnWindow, seats, pref }) {
  const sortedDays = (days || []).slice().sort((a, b) => (DAY_ORDER_KEY[a] ?? 9) - (DAY_ORDER_KEY[b] ?? 9));
  const dayStr = sortedDays.length ? sortedDays.map((d) => DAY_FULL[d] || d).join(" • ") : "—";
  const pickupStr = pickupWindow ? formatWindowLabel(pickupWindow) : "—";
  const returnStr = returnWindow ? formatWindowLabel(returnWindow) : "—";
  const seatsStr = seats === 5 ? "5+" : String(seats || 1);
  const prefStr = pref === "private" ? "Private ride" : pref === "shared" ? "Shared ride" : "—";
  return (
    <div
      className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 space-y-3"
      data-testid="sheet-plan-summary"
    >
      <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">
        Transportation plan
      </div>
      <div className="text-[15px] font-semibold tracking-wide text-[#F9F7F4]" data-testid="summary-days">
        {dayStr}
      </div>
      <div className="grid grid-cols-2 gap-3 text-[12px]">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#F9F7F4]/55">Pickup</div>
          <div className="mt-0.5 text-[#F9F7F4]" data-testid="summary-pickup">{pickupStr}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#F9F7F4]/55">Return</div>
          <div className="mt-0.5 text-[#F9F7F4]" data-testid="summary-return">{returnStr}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#F9F7F4]/55">Seats</div>
          <div className="mt-0.5 text-[#F9F7F4]" data-testid="summary-seats">{seatsStr}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#F9F7F4]/55">Transportation</div>
          <div className="mt-0.5 text-[#F9F7F4]" data-testid="summary-pref">{prefStr}</div>
        </div>
      </div>
    </div>
  );
}


/**
 * Legacy fallback for the rare consumer that does NOT supply plan setters —
 * keeps the original "recommended pickup" preview around so the slide-to-
 * confirm flow continues to work for backward-compat. The home flow now
 * uses plan-mode and never renders this.
 */
function PlanFallbackEarliest({ pickup, destination, duration, state }) {
  const [earliest, setEarliest] = useState(null);
  useEffect(() => {
    let stop = false;
    if (state !== "ready" || !duration) { setEarliest(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/scheduling/earliest-pickup", {
          params: {
            origin: pickup || undefined,
            destination: destination || undefined,
            duration_minutes: Math.round(duration),
            buffer_minutes: 10,
          },
        });
        if (stop) return;
        const ts = data?.timestamp ? new Date(data.timestamp) : null;
        if (!ts || isNaN(ts.getTime())) { setEarliest(null); return; }
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
        const base = new Date(ts); base.setHours(0, 0, 0, 0);
        const sameDay = base.getTime() === today.getTime();
        const isTomorrow = base.getTime() === tomorrow.getTime();
        const hh = ts.getHours(), mm = ts.getMinutes();
        const ampm = hh >= 12 ? "PM" : "AM";
        const h12 = ((hh + 11) % 12) + 1;
        const startLabel = `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
        const endTs = new Date(ts.getTime() + 15 * 60 * 1000);
        const eh = endTs.getHours(), em = endTs.getMinutes();
        const eAmpm = eh >= 12 ? "PM" : "AM";
        const eh12 = ((eh + 11) % 12) + 1;
        const endLabel = `${eh12}:${String(em).padStart(2, "0")} ${eAmpm}`;
        const timeLabel = `${startLabel} – ${endLabel}`;
        const dayLabel = sameDay ? "Today" : isTomorrow ? "Tomorrow" : ts.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        setEarliest({ human: sameDay ? timeLabel : `${dayLabel} ${timeLabel}` });
      } catch (_) {
        if (!stop) setEarliest(null);
      }
    })();
    return () => { stop = true; };
  }, [state, duration, pickup, destination]);
  return (
    <div className="text-[11.5px] text-[#F9F7F4]/80" data-testid="sheet-earliest-pickup">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[#0074d4] inline-block shrink-0" />
        <span>
          Recommended pickup:{" "}
          <span className="font-semibold text-[#F9F7F4]" data-testid="sheet-earliest-pickup-time">
            {earliest?.human || "—"}
          </span>
        </span>
      </div>
      <div className="text-[10px] text-[#F9F7F4]/55 mt-0.5 ml-3.5">Based on your arrival time</div>
    </div>
  );
}



/* ==========================================================================
   BookingWizard — B3.5 stepped booking flow (When → How → Review)
   ==========================================================================
   Replaces the flat, long-scroll "ready" panel. Each step fits within the
   sheet's height budget so no scrolling is required to reach the primary
   action at 390 / 430 / 768 widths.

   Step 1 · WHEN     — real upcoming-week date pills + pickup / return windows
   Step 2 · HOW      — seats stepper + shared/private preference + billing
   Step 3 · REVIEW   — summary card + promo banner (dynamic pct/$) + tap CTA

   Motion:
     · Steps slide directionally (x-axis). Reduced-motion collapses to fade.
     · Day pills use a subtle spring-scale on tap plus a light haptic
       (navigator.vibrate). Silently no-ops on unsupported platforms.
*/

const NEW_DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_KEY_FROM_JS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Build 7 upcoming dates starting today, each as { key: "Mon", label: "Mon", dateNum: 21 }. */
function upcomingWeekDates(base = new Date()) {
  const out = [];
  const start = new Date(base);
  start.setHours(0, 0, 0, 0);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push({
      key: DAY_KEY_FROM_JS[d.getDay()],
      label: DAY_KEY_FROM_JS[d.getDay()],
      dateNum: d.getDate(),
      iso: d.toISOString().slice(0, 10),
    });
  }
  return out;
}

function fireHaptic(pattern = 12) {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  } catch (_) { /* noop */ }
}

function BookingWizard({
  planMode, planReady, submitting,
  distance, duration, currentTier, previewRate,
  cycleTotal, firstRideDiscount, finalTotal, dailyRate,
  // Feb 2026 fare bundle — see ScheduleSetup /pricing/quote wiring.
  fare,
  days, setDays,
  pickupWindow, setPickupWindow,
  returnWindow, setReturnWindow,
  seats, setSeats,
  pref, setPref,
  cycle, setCycle, setCycleUserPicked,
  pickup, destination,
  firstRide,
  onConfirm,
  onCreateRoute,
  onPaymentSuccess,
  onExitToEntry, // hand-off from Step 0 Back → returns to "Where to?" entry surface
  // Hoisted step state — passed down from BookingSheet so it survives
  // the swipe-down-to-entry / swipe-up-return round trip.
  stepIdx,
  setStepIdx,
}) {
  const reduceMotion = useReducedMotion();
  const [dir, setDir] = useState(1); // +1 forward, -1 back
  const totalSteps = 3;

  // Step gates — advance is blocked until the current step is complete.
  const step1Ready = Array.isArray(days) && days.length > 0 && !!pickupWindow && !!returnWindow;
  const step2Ready = (seats || 1) >= 1 && (pref === "shared" || pref === "private");

  // P1 · in-sheet payment — mint the route the moment the rider enters
  // Step 3 so the Stripe intent fetch inside <StripeCheckoutSheet
  // chromeless> can start immediately. `routeId === null` while the POST
  // is in flight; ReviewStep shows a "Preparing payment…" slot.
  const [routeId, setRouteId] = useState(null);
  const [routeCreating, setRouteCreating] = useState(false);

  const goNext = async () => {
    if (stepIdx === 0 && !step1Ready) return;
    if (stepIdx === 1 && !step2Ready) return;
    if (stepIdx === 1 && typeof onCreateRoute === "function" && !routeId) {
      setRouteCreating(true);
      try {
        const rid = await onCreateRoute();
        if (rid) setRouteId(rid);
      } finally {
        setRouteCreating(false);
      }
    }
    setDir(1);
    setStepIdx((s) => Math.min(totalSteps - 1, s + 1));
    fireHaptic(10);
  };
  const goBack = () => {
    if (stepIdx === 0) {
      // Back from Step 1 (When do you commute?) returns the rider to
      // the address entry surface — hand off to ScheduleSetup via
      // `onExitToEntry` which unlocks the tier and resets the draw
      // phase so the sheet re-derives to `addressing`.
      if (typeof onExitToEntry === "function") onExitToEntry();
      fireHaptic(8);
      return;
    }
    setDir(-1);
    setStepIdx((s) => Math.max(0, s - 1));
    fireHaptic(8);
  };

  // Plan changes → invalidate the minted routeId so the next Step 2 →
  // Step 3 transition creates a fresh route against the updated payload.
  useEffect(() => {
    setRouteId(null);
  }, [JSON.stringify(days), pickupWindow, returnWindow, seats, pref, cycle]);

  // Re-mint the routeId reactively while the rider is ALREADY on Step 3
  // (Review & Pay) and something invalidated it (e.g. toggling Pay-once
  // ↔ Subscribe & save). Previously the only mint path lived inside
  // `goNext` — which only runs on Step 2 → Step 3 transitions — so
  // flipping the billing cycle on Step 3 left `routeId = null` forever
  // and the sheet stuck on "Preparing payment…".
  //
  // Testing-agent-driven rewrite (iteration_107):
  //   - Do NOT depend on `routeCreating` (it's set inside the effect,
  //     which re-triggers the effect and its own cleanup, canceling
  //     the awaited promise before it can commit routeId).
  //   - Do NOT depend on `onCreateRoute` (ScheduleSetup's useCallback
  //     re-derives when cycle changes → identity flip re-runs the
  //     effect prematurely). Route through a ref instead.
  //   - Use a ref-based in-flight guard so the effect body is
  //     idempotent regardless of how many re-renders happen while the
  //     mint POST is pending.
  const onCreateRouteRef = useRef(onCreateRoute);
  useEffect(() => { onCreateRouteRef.current = onCreateRoute; }, [onCreateRoute]);
  const remintRef = useRef({ inFlight: false });
  useEffect(() => {
    if (stepIdx !== 2) return;
    if (routeId) return;
    if (remintRef.current.inFlight) return;
    if (typeof onCreateRouteRef.current !== "function") return;
    remintRef.current.inFlight = true;
    setRouteCreating(true);
    (async () => {
      try {
        const rid = await onCreateRouteRef.current();
        if (rid) setRouteId(rid);
      } finally {
        remintRef.current.inFlight = false;
        setRouteCreating(false);
      }
    })();
  }, [stepIdx, routeId, cycle]);

  // Feb 2026 rebuild — the server's `fare.cycle_total` is now the
  // canonical charged amount (party × volume + subscribe × first-ride
  // override × floor). We MUST NOT subtract firstRideDiscount from it
  // here — the discount is ALREADY baked in when `first_ride_wins`.
  // Previously this memo double-subtracted the 15% and the rider saw a
  // price 15% lower than what /pay/intent actually charges (P0
  // violation of the Feb 2026 spec: "PaymentIntent must equal the
  // displayed total"). `firstRideDiscount` still flows through to the
  // Review step as an INFORMATIONAL savings badge.
  const discountedTotal = useMemo(() => Number(finalTotal || 0), [finalTotal]);

  const priceReady = Number(cycleTotal || 0) > 0 && Array.isArray(days) && days.length > 0;

  // Framer variants — respects prefers-reduced-motion.
  const stepVariants = reduceMotion
    ? {
        enter: { opacity: 0 },
        center: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        enter: (d) => ({ x: d > 0 ? 40 : -40, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (d) => ({ x: d > 0 ? -40 : 40, opacity: 0 }),
      };
  const stepTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.32, ease: [0.32, 0.72, 0, 1] };

  // Upcoming week dates (memoised so we don't reflow on every re-render).
  const weekDates = useMemo(() => upcomingWeekDates(), []);

  const stepLabels = ["When", "How", "Review & Pay"];
  const isPaymentStep = stepIdx === 2;

  return (
    <div className="pt-1" data-testid="sheet-ready">
      {/* Hero headline per step — 2-line height. The wrapping <h1>
          reserves enough top space (paddingTop) so the FIRST line clears
          the route chip that lives above the wizard viewport. Prior
          test relied on scrollHeight === clientHeight which is always
          true inside an overflow:hidden container — so top clipping
          went undetected. New acceptance measures child bounding rects
          vs container bounds instead. */}
      <h1
        className="mb-3"
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 900,
          fontSize: 36,
          lineHeight: 1.05,
          letterSpacing: "-1.2px",
          color: "#fff",
          paddingTop: 8,
        }}
        data-testid={`wizard-hero-${stepIdx}`}
      >
        {stepIdx === 0 && (
          <>When do you <span style={{ background: "linear-gradient(100deg,#4B9CF8,#ff7d22)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent" }}>commute?</span></>
        )}
        {stepIdx === 1 && (
          <>How would you like to <span style={{ background: "linear-gradient(100deg,#4B9CF8,#ff7d22)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent" }}>ride?</span></>
        )}
        {stepIdx === 2 && (
          <>Review &amp; <span style={{ background: "linear-gradient(100deg,#4B9CF8,#ff7d22)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent" }}>pay</span></>
        )}
      </h1>

      {/* Sliding step viewport — minHeight kept so the sheet bounds
          don't reshape between step transitions. */}
      <div className="relative" style={{ minHeight: 400 }}>
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div
            key={stepIdx}
            custom={dir}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={stepTransition}
            data-testid={`wizard-step-${stepIdx}`}
            data-step-id={["when", "how", "review"][stepIdx]}
          >
            {stepIdx === 0 && (
              <WhenStep
                weekDates={weekDates}
                days={days}
                setDays={setDays}
                pickupWindow={pickupWindow} setPickupWindow={setPickupWindow}
                returnWindow={returnWindow} setReturnWindow={setReturnWindow}
                reduceMotion={reduceMotion}
                fare={fare}
                pickup={pickup}
                destination={destination}
              />
            )}
            {stepIdx === 1 && (
              <HowStep
                seats={seats} setSeats={setSeats}
                pref={pref} setPref={setPref}
                fare={fare}
              />
            )}
            {stepIdx === 2 && (
              <ReviewStep
                distance={distance} duration={duration}
                currentTier={currentTier}
                cycleTotal={cycleTotal}
                firstRideDiscount={firstRideDiscount}
                discountedTotal={discountedTotal}
                pickup={pickup} destination={destination}
                days={days} pickupWindow={pickupWindow} returnWindow={returnWindow}
                seats={seats} pref={pref} cycle={cycle} setCycle={setCycle} setCycleUserPicked={setCycleUserPicked}
                firstRide={firstRide}
                routeId={routeId}
                routeCreating={routeCreating}
                onPaymentSuccess={onPaymentSuccess}
                onEditRoute={() => { setDir(-1); setStepIdx(0); }}
                onEditSchedule={() => { setDir(-1); setStepIdx(0); }}
                onEditRide={() => { setDir(-1); setStepIdx(1); }}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Price row — Steps 1 & 2 only. Bare row (no card), align-center
          so the label BLOCK centers against the number. 32px 900 amount
          with muted unit; label carries a subline that reflects the
          current selection. Reduced-motion snaps. */}
      {!isPaymentStep && (
      <div
        className="mt-3 flex items-center justify-between"
        style={{ fontFamily: "var(--font-display)", padding: "6px 2px" }}
        data-testid="wizard-price-row"
      >
        <div className="min-w-0 flex flex-col justify-center" style={{ gap: 5 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#7b87a6", letterSpacing: "0.7px" }}>
            {cycle === "payg" ? "TODAY’S TOTAL" : "WEEKLY TOTAL"}
          </div>
          <div
            style={{
              fontFamily: "var(--font-body)", fontWeight: 400, fontSize: 11.5,
              color: "#7b87a6", lineHeight: 1,
            }}
          >
            {(!days || days.length === 0)
              ? "Select days"
              : `${days.length} day${days.length === 1 ? "" : "s"} · ${pref === "private" ? "private" : "round-trip"}`}
          </div>
        </div>
        <div className="flex items-baseline">
          {priceReady ? (
            <>
              <PriceOdometer value={discountedTotal} reduceMotion={reduceMotion} />
              {cycle === "weekly" && (
                <span style={{ fontWeight: 700, fontSize: 12, color: "#7b87a6", marginLeft: 4 }}>/wk</span>
              )}
            </>
          ) : (
            <span
              style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 32, color: "rgba(255,255,255,0.35)" }}
              data-testid="wizard-price-placeholder"
            >—</span>
          )}
        </div>
      </div>
      )}

      {/* Bottom navigation — On Step 3 (Review & Pay) the wizard hands
          the CTA off to the embedded <StripeCheckoutSheet chromeless>,
          which owns the wallet button + card-form submit. Only Back
          stays visible. */}
      <div className="mt-3 flex items-center gap-2" data-testid="wizard-nav">
        {/* Back is ALWAYS visible in the wizard. On Step 0 it hands off
            to ScheduleSetup via `onExitToEntry`, unlocking the tier and
            returning the rider to the "Where to?" address entry
            surface. On later steps it walks the wizard backwards. */}
        <button
          type="button"
          onClick={goBack}
          className="h-12 px-4 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-[#F9F7F4]/85 text-[13px] font-semibold tracking-wide flex items-center gap-1.5 transition-colors"
          style={{ fontFamily: "var(--font-display)", letterSpacing: "0.02em" }}
          data-testid="wizard-back-button"
          aria-label="Back"
        >
          <ArrowLeft size={14} strokeWidth={2.2} />
          Back
        </button>
        {!isPaymentStep && (
          <button
            type="button"
            onClick={goNext}
            disabled={(stepIdx === 0 && !step1Ready) || (stepIdx === 1 && !step2Ready) || routeCreating}
            className="flex-1 h-12 rounded-full bg-[#ed6500] hover:bg-[#ff7615] disabled:bg-[#ed6500]/25 disabled:text-[#F9F7F4]/40 text-white text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors"
            style={{ fontFamily: "var(--font-display)", letterSpacing: "0.02em" }}
            data-testid="wizard-next-button"
          >
            {routeCreating ? "Loading…" : "Continue"}
            {!routeCreating && <ArrowRight size={15} strokeWidth={2.2} />}
          </button>
        )}
      </div>
    </div>
  );
}

/* ---- PriceOdometer — inline digit-roll for the pinned price row.
   Splits the amount into a fixed "$" prefix + rolling digits so only
   the numeric characters animate. Reduced-motion snaps to the value. */
function PriceOdometer({ value, reduceMotion }) {
  const formatted = `$${Number(value || 0).toFixed(2)}`;
  return (
    <span
      className="tabular-nums flex items-baseline"
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: 900,
        fontSize: 32,
        letterSpacing: "-1.1px",
        color: "#fff",
        lineHeight: 1,
      }}
      data-testid="wizard-price-amount"
      data-value={value}
    >
      <span aria-hidden="true">$</span>
      {formatted.slice(1).split("").map((ch, i) => (
        <OdometerDigit key={i} ch={ch} reduceMotion={reduceMotion} />
      ))}
    </span>
  );
}

function OdometerDigit({ ch, reduceMotion }) {
  // Non-digit characters (the ".") don't animate — they just render.
  if (!/[0-9]/.test(ch)) return <span aria-hidden="true">{ch}</span>;
  if (reduceMotion) return <span>{ch}</span>;
  // Baseline-preserving digit box. The invisible in-flow "0" spacer
  // gives the inline-block a real character baseline that matches the
  // motion.span content's baseline (both are the same font/size). This
  // is what fixes the "$00.00" misalignment on the wizard price row —
  // without the spacer the inline-block's baseline defaults to its
  // bottom margin edge, which sits ~0.2em BELOW a regular digit's
  // baseline, so the animated digits floated visibly higher than the
  // "$" and the "." on the same line.
  return (
    <span
      className="relative inline-block overflow-hidden text-center"
      style={{ width: "0.62em", verticalAlign: "baseline" }}
    >
      <span aria-hidden="true" style={{ visibility: "hidden" }}>0</span>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={ch}
          initial={{ y: "-0.7em", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "0.7em", opacity: 0 }}
          transition={{
            // Buttery drop-from-above odometer. Longer duration + a
            // custom cubic-bezier that eases-out with a soft landing
            // gives each digit a mechanical-odometer weight instead of
            // the snappy pop it had at 0.28s. The opacity fade is
            // faster than the y so the arriving digit doesn't ghost
            // through the departing one mid-slot.
            y: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
            opacity: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
          }}
          className="absolute inset-0 text-center"
        >
          {ch}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/* ---- Step 1 · WHEN ---- */
function WhenStep({ weekDates, days, setDays, pickupWindow, setPickupWindow, returnWindow, setReturnWindow, reduceMotion, fare, pickup, destination }) {
  const toggleDay = (key) => {
    fireHaptic(12);
    const has = (days || []).includes(key);
    const next = has ? (days || []).filter((x) => x !== key) : [...(days || []), key];
    next.sort((a, b) => NEW_DAY_ORDER.indexOf(a) - NEW_DAY_ORDER.indexOf(b));
    setDays(next);
  };

  // Feb 2026 · arrival-anchored scheduling. The wheel values are the
  // rider's ARRIVE BY / LEAVE WORK. We compute the pickup window
  // backward using the traffic-aware endpoint, debounced so wheel
  // spins stay responsive. Values may arrive as legacy "HH:MM-HH:MM"
  // window ranges; normalize to the start-time HH:MM for the fetch
  // and for `formatTime12` output.
  const stripRange = (v) => (v ? String(v).split("-")[0] : v);
  const arriveBy = stripRange(pickupWindow) || "07:30";
  const leaveWork = stripRange(returnWindow) || "17:00";
  const [estimate, setEstimate] = React.useState(null);
  const estimateTimer = React.useRef(null);
  React.useEffect(() => {
    if (!pickup || !destination || !arriveBy) return;
    if (estimateTimer.current) clearTimeout(estimateTimer.current);
    estimateTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/scheduling/pickup-window-estimate", {
          origin: pickup,
          destination,
          arrival_time: arriveBy,
          return_time: leaveWork,
          tz_offset_minutes: -new Date().getTimezoneOffset(),
        });
        setEstimate(data);
      } catch {
        // Silent — the disclosure falls back to a generic "around" copy.
        setEstimate(null);
      }
    }, 350);
    return () => { if (estimateTimer.current) clearTimeout(estimateTimer.current); };
  }, [pickup, destination, arriveBy, leaveWork]);

  // Operating-hour narrowing — an arrival of 4:15 AM implies a pickup
  // around 3:50 AM (before we operate). Push the earliest selectable
  // ARRIVE BY forward to (04:00 + drive + buffer). Similarly the
  // latest selectable LEAVE WORK is (20:00 − return drive).
  //
  // OPERATING WINDOW: 04:00 – 20:00 (matches START_HOUR/END_HOUR in
  // TimeWheelPair). We keep those constants co-located with the wheel
  // and derive per-route caps here from the live estimate.
  const OP_OPEN_MIN = 4 * 60;
  const OP_CLOSE_MIN = 20 * 60;
  const hhmmToMin = (s) => {
    if (!s) return null;
    const [h, m] = String(s).split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };
  const minToHHMM = (m) => {
    const clamped = Math.max(0, Math.min(24 * 60 - 1, m));
    const h = Math.floor(clamped / 60);
    const mm = clamped % 60;
    return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };
  const driveOut = estimate?.outbound?.drive_minutes || null;
  const bufferMin = estimate?.outbound?.arrival_buffer_min ?? 10;
  const driveRet = estimate?.return?.drive_minutes || null;
  const pickupMin = driveOut != null ? minToHHMM(OP_OPEN_MIN + driveOut + bufferMin) : null;
  const returnMax = driveRet != null ? minToHHMM(OP_CLOSE_MIN - driveRet) : null;
  // Inline reason lines — only surface when the constraint narrows
  // beyond operating hours (route makes early arrival / late leave
  // unreachable).
  const showEarliestReason = pickupMin && hhmmToMin(pickupMin) > OP_OPEN_MIN;
  const showLatestReason = returnMax && hhmmToMin(returnMax) < OP_CLOSE_MIN;

  // Auto-snap: if a live drive update makes the current arrival/leave
  // fall outside the narrowed range, nudge state to the nearest edge
  // so the wheel display and downstream fetch stay in sync.
  React.useEffect(() => {
    if (!pickupMin) return;
    if (hhmmToMin(arriveBy) < hhmmToMin(pickupMin)) {
      setPickupWindow(pickupMin);
    }
  }, [pickupMin, arriveBy, setPickupWindow]);
  React.useEffect(() => {
    if (!returnMax) return;
    if (hhmmToMin(leaveWork) > hhmmToMin(returnMax)) {
      setReturnWindow(returnMax);
    }
  }, [returnMax, leaveWork, setReturnWindow]);

  return (
    <div className="space-y-6" data-testid="wizard-when">
      {/* Day tiles — surfaces (no border on unselected; orange gradient
          when selected). Values from step1e.html .day / .day.on. */}
      <div className="flex" style={{ gap: 7, fontFamily: "var(--font-display)" }} data-testid="plan-days">
        {weekDates.map((d) => {
          const active = (days || []).includes(d.key);
          return (
            <button
              key={d.iso}
              type="button"
              onClick={() => toggleDay(d.key)}
              className="flex-1 min-w-0 flex flex-col items-center justify-center"
              style={{
                padding: "12px 0 13px",
                borderRadius: 16,
                background: active
                  ? "linear-gradient(165deg, #ff7d22, #c9550b)"
                  : "rgba(255,255,255,0.055)",
                boxShadow: active
                  ? "0 10px 26px rgba(237,102,3,0.45)"
                  : "0 6px 20px rgba(0,0,0,0.30)",
              }}
              data-testid={`plan-day-${d.key}`}
              data-day-active={active ? "true" : "false"}
              aria-pressed={active}
            >
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 700, fontSize: 9, letterSpacing: "0.7px",
                  color: active ? "rgba(255,255,255,0.9)" : "#7b87a6",
                }}
              >{d.label}</span>
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 800, fontSize: 16, marginTop: 2,
                  color: active ? "#fff" : "#dbe2f0",
                }}
              >{d.dateNum}</span>
            </button>
          );
        })}
      </div>

      {/* Two side-by-side fields, EACH with its own hour/min/AM-PM wheel.
          Six independent scrollers on screen (see TimeWheelPair). We
          strip any legacy "-HH:MM" range suffix so the wheel receives
          a clean HH:MM start time. Per-wheel min/max come from the
          live estimate so operating-hour narrowing (arrival-anchoring)
          is enforced at the value layer. */}
      <TimeWheelPair
        pickup={arriveBy}
        setPickup={setPickupWindow}
        returnv={leaveWork}
        setReturn={setReturnWindow}
        pickupMin={pickupMin || undefined}
        returnMax={returnMax || undefined}
        testId="wheel-pair"
      />

      {(showEarliestReason || showLatestReason) && (
        <div
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 400, fontSize: 12, lineHeight: 1.45, color: "#96a2c0",
            paddingLeft: 4,
          }}
          data-testid="when-hours-reason"
        >
          {showEarliestReason && (
            <div data-testid="when-earliest-reason">
              Earliest arrival for this route is{" "}
              <b style={{ color: "#e9eef7", fontWeight: 600 }}>{formatTime12(pickupMin)}</b>.
            </div>
          )}
          {showLatestReason && (
            <div data-testid="when-latest-reason">
              Latest leave for this route is{" "}
              <b style={{ color: "#e9eef7", fontWeight: 600 }}>{formatTime12(returnMax)}</b>.
            </div>
          )}
        </div>
      )}

      {/* Feb 2026 · arrival-anchored disclosure. The rider says when to
          ARRIVE BY — Link computes the pickup window backward using
          traffic-aware Google Routes v2 and shows it as an ESTIMATE.
          The word "around" is required until the T-48h freeze
          confirmation replaces the window with an exact minute. */}
      <div data-testid="when-disclosure">
        <div
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 400, fontSize: 13.5, lineHeight: 1.5, color: "#e9eef7",
            display: "flex", alignItems: "flex-start", gap: 6,
          }}
        >
          <span style={{ color: "#ff7d22", fontWeight: 700, marginTop: 1 }} aria-hidden="true">↑</span>
          <span>
            Pickup around{" "}
            <b style={{ fontWeight: 700, color: "#ffffff" }}>
              {estimate?.outbound?.pickup_window
                ? formatDrumWindow(estimate.outbound.pickup_window)
                : formatDrumWindow(arriveBy)}
            </b>{" "}
            to arrive by <b style={{ fontWeight: 700, color: "#ffffff" }}>{formatTime12(arriveBy)}</b>{" "}
            <span style={{ color: "#96a2c0", fontStyle: "italic" }}>· based on today&rsquo;s traffic</span>
          </span>
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily: "var(--font-body)",
            fontWeight: 400, fontSize: 12, lineHeight: 1.5, color: "#96a2c0",
            paddingLeft: 18,
          }}
        >
          We confirm your exact pickup time 48 hours before your first ride.
        </div>
      </div>

      {/* Feb 2026 · Live rate breakdown for Step 1. Fills the dead
          space between the pickup-window note and the price row so
          the price odometer is explained rather than mysterious. */}
      <RateBreakdownWhen fare={fare} days={days} />
    </div>
  );
}

/* ---- Step 2 · HOW · restructured to step2b.html spec ---- */
function HowStep({ seats, setSeats, pref, setPref, fare }) {
  const isPrivate = pref === "private";
  // Feb 2026 caps: Shared 3, Private 4. Auto-upgrade the rider to
  // Private when they push past the Shared cap so we never send an
  // invalid party size to /pay/intent (which would 400).
  const maxSeats = isPrivate ? 4 : 3;
  const clamp = (n, cap) => Math.max(1, Math.min(cap, n));
  const handleMinus = () => setSeats(clamp((seats || 1) - 1, maxSeats));
  const handlePlus = () => {
    const next = (seats || 1) + 1;
    if (!isPrivate && next > 3) {
      // Pushing beyond 3 on Shared → hop into Private at 4.
      setPref("private");
      setSeats(4);
      return;
    }
    setSeats(clamp(next, maxSeats));
  };
  const handlePrefChange = (v) => {
    setPref(v);
    if (v === "shared" && (seats || 1) > 3) setSeats(3);
    if (v === "private" && (seats || 1) > 4) setSeats(4);
  };
  return (
    <div className="space-y-4" data-testid="wizard-how">
      <div>
        <SegmentedPill
          testId="plan-preference"
          options={[
            { key: "shared",  label: "Shared" },
            { key: "private", label: "Private" },
          ]}
          value={isPrivate ? "private" : "shared"}
          onChange={handlePrefChange}
        />
        {/* Dynamic description below the pill — swaps with selection. */}
        <div
          className="mt-3"
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 400, fontSize: 12.5, lineHeight: 1.5, color: "#96a2c0",
            padding: "0 2px",
          }}
          data-testid="preference-description"
        >
          {isPrivate
            ? "Reserved exclusively for your group."
            : "May travel with other riders going the same way · best price"}
        </div>
      </div>
      <div>
        <div
          className="mb-2.5"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700, fontSize: 10, letterSpacing: "2px", color: "#7b87a6",
          }}
        >
          SEATS
        </div>
        {/* Full-width -/+ stepper (step2b.html .seats). */}
        <div
          className="flex items-center justify-between"
          style={{
            background: "rgba(255,255,255,0.055)",
            borderRadius: 20,
            padding: "10px 14px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.30)",
          }}
          data-testid="plan-seats"
        >
          <button
            type="button"
            onClick={handleMinus}
            className="flex items-center justify-center"
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: "rgba(255,255,255,0.10)",
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22,
              color: "#fff",
            }}
            data-testid="plan-seats-minus"
            aria-label="Decrease seats"
          >−</button>
          <div
            className="flex-1 text-center"
            style={{
              fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 22,
              color: "#fff", letterSpacing: "-0.5px",
            }}
            data-testid="plan-seats-value"
          >
            {String(seats || 1)}{" "}
            <span style={{ fontWeight: 700, fontSize: 12, color: "#96a2c0", letterSpacing: "1.4px" }}>
              SEAT{(seats || 1) === 1 ? "" : "S"}
            </span>
          </div>
          <button
            type="button"
            onClick={handlePlus}
            className="flex items-center justify-center"
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: "rgba(255,255,255,0.10)",
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22,
              color: "#fff",
            }}
            data-testid="plan-seats-plus"
            aria-label="Increase seats"
          >+</button>
        </div>
      </div>
      {/* Feb 2026 · Live rate breakdown for Step 2. Shared shows the
          half-fare addition per additional rider; Private replaces
          with what Private buys. */}
      <RateBreakdownHow fare={fare} seats={seats} pref={pref} />
    </div>
  );
}

/* ---- Feb 2026 · Live rate breakdown ("receipt-in-progress") ----
   Renders below the last control on Steps 1 and 2, updating live as
   the rider adjusts days / seats / ride type / billing cycle. Reads
   the server-computed `fare` bundle so what's shown here reconciles
   to the PaymentIntent Stripe charges. Styling per spec: no card
   border, muted Inter labels on the left, Montserrat figures right-
   aligned, receipt-in-progress feel — not another boxed component. */
function fmtUsd(n) {
  const v = Number(n || 0);
  return `$${v.toFixed(2)}`;
}
function volumeLabel(rate, n_days) {
  if (rate <= 0) return "No volume discount yet";
  const pct = Math.round(rate * 100);
  return `${pct}% volume discount (${n_days} day${n_days === 1 ? "" : "s"})`;
}
function RateLine({ label, value, muted, testId, dim }) {
  return (
    <div
      className="flex items-baseline justify-between"
      style={{ padding: "6px 2px", opacity: dim ? 0.55 : 1 }}
      data-testid={testId}
    >
      <span
        style={{
          fontFamily: "var(--font-body)",
          fontWeight: 400,
          fontSize: 12.5,
          letterSpacing: "0.01em",
          color: muted ? "#96a2c0" : "#dbe2f0",
          lineHeight: 1.4,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: muted ? 600 : 800,
          fontSize: muted ? 13 : 15,
          color: muted ? "#96a2c0" : "#F9F7F4",
          letterSpacing: "-0.2px",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}
function RateDivider() {
  return (
    <div
      style={{
        height: 1,
        background:
          "linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)",
        margin: "4px 0",
      }}
    />
  );
}

/* Step 1 breakdown — base rate → volume discount applied → resulting
   daily rate. Plus a quiet nudge line when 1 more day reaches the
   next band ("Add 1 more day → $23.84/day"). At 5+ days: "Best rate —
   full-week commitment." */
function RateBreakdownWhen({ fare, days = [] }) {
  if (!fare || !Number.isFinite(fare.daily_fare) || fare.daily_fare <= 0) return null;
  const n = Array.isArray(days) ? days.length : Number(days || 0);
  if (n <= 0) return null;
  const base = fare.daily_before_discount;
  const daily = fare.daily_fare;
  const volumeRate = fare.volume_rate;
  const subscribe = fare.subscribe_bonus;
  const firstRideWins = fare.first_ride_wins;
  const combinedCapped = fare.combined_capped;
  const nudge = fare.nudge_next_band;
  const savings = Math.max(0, +(base - daily).toFixed(2));

  let discountLabel;
  if (firstRideWins) {
    discountLabel = "First-ride discount (15%)";
  } else if (subscribe > 0 && volumeRate > 0) {
    discountLabel = `${Math.round(volumeRate * 100)}% volume + 3% subscribe${combinedCapped ? " (capped at 15%)" : ""}`;
  } else if (subscribe > 0) {
    discountLabel = "3% subscribe & save";
  } else if (volumeRate > 0) {
    discountLabel = volumeLabel(volumeRate, n);
  } else {
    discountLabel = "No volume discount yet";
  }

  return (
    <div className="mt-3" data-testid="rate-breakdown-when">
      <RateLine
        label="Base rate"
        value={`${fmtUsd(base)}/day`}
        testId="rate-when-base"
      />
      <RateLine
        label={discountLabel}
        value={savings > 0 ? `−${fmtUsd(savings)}/day` : "—"}
        muted
        dim={savings <= 0}
        testId="rate-when-discount"
      />
      <RateDivider />
      <RateLine
        label="Daily rate"
        value={`${fmtUsd(daily)}/day`}
        testId="rate-when-daily"
      />
      {/* Quiet nudge line — body text, no badge, no colour flash.
          Always renders (independent of first-ride status) so riders
          can preview the fully-discounted rate at the next band. */}
      {nudge && (
        <div
          className="mt-1.5"
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 1.45,
            color: "#96a2c0",
            letterSpacing: "0.01em",
            padding: "2px 2px 0",
          }}
          data-testid="rate-when-nudge"
        >
          Add {nudge.delta_days} more day{nudge.delta_days === 1 ? "" : "s"} → {fmtUsd(nudge.target_daily)}/day
        </div>
      )}
      {!nudge && (firstRideWins || n >= 5) && (
        <div
          className="mt-1.5"
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 1.45,
            color: "#96a2c0",
            letterSpacing: "0.01em",
            padding: "2px 2px 0",
          }}
          data-testid="rate-when-best"
        >
          {firstRideWins
            ? "First-ride discount applied — you're already at your best rate."
            : "Best rate — full-week commitment."}
        </div>
      )}
    </div>
  );
}

/* Step 2 breakdown — SHARED: line per rider showing the half-fare
   addition, then the new daily total. PRIVATE: replace with what
   Private buys (whole vehicle, up to 4 riders, exact pickup time
   instead of a 15-minute window). */
function RateBreakdownHow({ fare, seats = 1, pref = "shared" }) {
  if (!fare || !Number.isFinite(fare.daily_fare) || fare.daily_fare <= 0) return null;
  if (pref === "private") {
    return (
      <div className="mt-3" data-testid="rate-breakdown-how">
        <RateLine
          label="Whole vehicle"
          value="Reserved for you"
          muted
          testId="rate-how-private-whole"
        />
        <RateLine
          label="Party size"
          value="Up to 4 riders"
          muted
          testId="rate-how-private-capacity"
        />
        <RateLine
          label="Pickup"
          value="Exact time (no 15-min window)"
          muted
          testId="rate-how-private-window"
        />
        <RateDivider />
        <RateLine
          label="Daily rate"
          value={`${fmtUsd(fare.daily_fare)}/day`}
          testId="rate-how-daily"
        />
      </div>
    );
  }
  // Shared: base + half-fare additions → daily total.
  const base = fare.daily_base;             // 1-rider Shared base (pre-mult)
  const daily = fare.daily_fare;             // post party × discount × floor
  const s = Math.max(1, Math.min(3, Number(seats || 1)));
  const additions = [];
  for (let i = 2; i <= s; i += 1) {
    additions.push({ n: i, add: +(base * 0.5).toFixed(2) });
  }
  return (
    <div className="mt-3" data-testid="rate-breakdown-how">
      <RateLine
        label="Base fare (1 rider)"
        value={`${fmtUsd(base)}/day`}
        testId="rate-how-base"
      />
      {additions.map((a) => (
        <RateLine
          key={a.n}
          label={`+ Rider ${a.n} (+50%)`}
          value={`+${fmtUsd(a.add)}/day`}
          muted
          testId={`rate-how-rider-${a.n}`}
        />
      ))}
      <RateDivider />
      <RateLine
        label="Daily rate"
        value={`${fmtUsd(daily)}/day`}
        testId="rate-how-daily"
      />
    </div>
  );
}

/* ---- SegmentedPill — reused by Step 2 (ride type) + Step 3 (billing).
   Exact values from step2b.html / step3b.html .seg / .sopt. */
function SegmentedPill({ options, value, onChange, testId }) {
  return (
    <div
      className="flex"
      style={{
        background: "rgba(255,255,255,0.05)",
        borderRadius: 16,
        padding: 4,
        boxShadow: "0 6px 20px rgba(0,0,0,0.30)",
      }}
      data-testid={testId}
    >
      {options.map((opt) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange?.(opt.key)}
            className="flex-1 text-center"
            style={{
              padding: "12px 0",
              borderRadius: 12,
              fontFamily: "var(--font-display)",
              fontWeight: active ? 800 : 700,
              fontSize: 14,
              color: active ? "#fff" : "#96a2c0",
              background: active
                ? "linear-gradient(165deg, #ff7d22, #c9550b)"
                : "transparent",
              boxShadow: active ? "0 8px 20px rgba(237,102,3,0.4)" : "none",
              transition: "background 240ms cubic-bezier(0.16,1,0.3,1)",
            }}
            data-testid={`${testId}-${opt.key}`}
            aria-pressed={active}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---- Step 3 · REVIEW & PAY ----
   Combines the tap-to-edit summary rows (Route/Schedule/Ride), the
   discounted price block, and the in-sheet Stripe payment surface. The
   rider never leaves the sheet — <StripeCheckoutSheet chromeless> is
   rendered inline as a descendant of this component. Its own <Elements>
   provider wraps the wallet buttons and card form, so
   PaymentRequestButtonElement is guaranteed a descendant of the provider
   (verified in the acceptance run). Intent is fetched only after the
   rider reaches this step and the parent has minted a routeId. */
function ReviewStep({
  distance, duration, currentTier,
  cycleTotal, firstRideDiscount, discountedTotal,
  pickup, destination,
  days, pickupWindow, returnWindow, seats, pref, cycle, setCycle, setCycleUserPicked,
  firstRide,
  routeId, routeCreating, onPaymentSuccess,
  onEditRoute, onEditSchedule, onEditRide,
}) {
  const daysStr = (days || []).slice().sort(
    (a, b) => NEW_DAY_ORDER.indexOf(a) - NEW_DAY_ORDER.indexOf(b),
  ).join(" · ") || "—";

  const total = Number(discountedTotal || 0);
  const undiscounted = Number(cycleTotal || 0);
  const discount = Number(firstRideDiscount || 0);
  const showDiscount = discount > 0 && undiscounted > total;

  // Dynamic promo banner — copy is derived from the actual coupon logic
  // exposed by /api/pricing/first-ride. Never hardcoded.
  const showBanner = !!firstRide && firstRide.eligible;
  let bannerText = null;
  if (showBanner) {
    if (firstRide.discount_type === "percent" && Number(firstRide.discount_pct) > 0) {
      const pct = Math.round(Number(firstRide.discount_pct) * 100);
      bannerText = `${pct}% off your first ride`;
    } else if (firstRide.discount_type === "fixed" && Number(firstRide.discount_amount_cents) > 0) {
      const amt = (Number(firstRide.discount_amount_cents) / 100).toFixed(0);
      bannerText = `$${amt} off your first ride`;
    }
  }

  const isPayg = cycle === "payg";
  return (
    <div className="space-y-3" data-testid="wizard-review">
      {/* BILLING segmented pill — matches Step 2's ride-type pill.
          Description below swaps dynamically with selection. */}
      <div>
        <div
          className="mb-2.5"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700, fontSize: 10, letterSpacing: "2px", color: "#7b87a6",
          }}
        >
          BILLING
        </div>
        <SegmentedPill
          testId="review-billing"
          options={[
            { key: "payg",   label: "Pay once" },
            { key: "weekly", label: "Subscribe & save" },
          ]}
          value={isPayg ? "payg" : "weekly"}
          onChange={(v) => { setCycle?.(v); setCycleUserPicked?.(true); }}
        />
        <div
          className="mt-2"
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 400, fontSize: 12.5, lineHeight: 1.5, color: "#96a2c0",
            padding: "0 2px",
          }}
          data-testid="review-billing-description"
        >
          {isPayg
            ? "One-time charge today for the days you selected."
            : "Auto-renews weekly · cancel anytime."}
        </div>
      </div>

      {/* Price block — orange-tinted card per step3b.html .pb.
          Subtotal (struck through) → discount line → RIDE TOTAL. */}
      <div
        data-testid="sheet-price-preview"
        style={{
          background: "rgba(237,102,3,0.10)",
          borderRadius: 20,
          padding: "15px 17px",
          boxShadow: "0 12px 30px rgba(237,102,3,0.15), inset 0 0 0 1px rgba(237,102,3,0.38)",
        }}
      >
        {showBanner && bannerText && (
          <div
            style={{
              fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12,
              color: "#3ecf8e", marginBottom: 10,
            }}
            data-testid="welcome-offer-banner-review"
          >
            {bannerText}
          </div>
        )}
        {showDiscount && (
          <div className="flex items-baseline justify-between" style={{ marginBottom: 6 }} data-testid="review-price-strike-row">
            <span style={{ fontFamily: "var(--font-body)", fontWeight: 400, fontSize: 13, color: "#a0accb" }}>Subtotal</span>
            <span
              className="tabular-nums"
              style={{
                fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, color: "#a0accb",
                textDecoration: "line-through",
              }}
            >${undiscounted.toFixed(2)}</span>
          </div>
        )}
        {showDiscount && (
          <div className="flex items-baseline justify-between" style={{ marginBottom: 6 }} data-testid="review-price-discount-row">
            <span style={{ fontFamily: "var(--font-body)", fontWeight: 400, fontSize: 13, color: "#a0accb" }}>First-ride discount</span>
            <span
              className="tabular-nums"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, color: "#3ecf8e" }}
            >−${discount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex items-center justify-between" style={{ marginTop: 11 }}>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 10, letterSpacing: "2px", color: "#fff" }}>
            RIDE TOTAL
          </span>
          <span
            className="tabular-nums"
            style={{
              fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 32,
              letterSpacing: "-1.1px", color: "#fff", lineHeight: 1,
            }}
            data-testid="sheet-price-amount"
          >
            ${total.toFixed(2)}
          </span>
        </div>
      </div>

      {/* P1 · Payment methods embedded in-sheet — Elements provider
          untouched, wallet feature-detected via canMakePayment(). */}
      <div data-testid="review-payment-slot">
        {routeCreating || !routeId ? (
          <div
            className="rounded-2xl bg-white/[0.04] border border-white/10 px-4 py-4 text-[12px] text-white/60"
            data-testid="review-payment-loading"
          >
            Preparing payment…
          </div>
        ) : (
          <StripeCheckoutSheet
            chromeless
            open
            hideCloseButton
            onSuccess={onPaymentSuccess}
            onFallback={(reason) => { console.warn("[review-step] stripe fallback:", reason); }}
            onClose={() => { /* wizard Back owns dismiss */ }}
            routeId={routeId}
            billingCycle={cycle || "weekly"}
            amountDisplay={`$${total.toFixed(2)}`}
            subtitle={`${isPayg ? "One-time" : "Weekly"} · ${daysStr}`}
            ctaLabel={`Pay $${total.toFixed(2)}`}
          />
        )}
      </div>
    </div>
  );
}
